import { useState, useEffect, useCallback, useRef } from 'react';
import { webSocketManager } from '../services/webSocketManager';
import { debugLogger } from '../utils/debugLogger';

export interface UseWebRTCSignalingOptions {
  roomId: string;
  userId: string;
  roomType: 'livestream' | 'call';
  onOffer?: (senderId: string, offer: RTCSessionDescriptionInit) => void;
  onAnswer?: (senderId: string, answer: RTCSessionDescriptionInit) => void;
  onIceCandidate?: (senderId: string, candidate: RTCIceCandidateInit) => void;
  onUserJoined?: (userId: string) => void;
  onUserLeft?: (userId: string) => void;
  onError?: (error: any) => void;
}

/**
 * WebRTC signaling hook using singleton WebSocket manager
 * Reuses existing connection instead of creating new ones
 */
export const useWebRTCSignaling = (options: UseWebRTCSignalingOptions) => {
  const {
    roomId,
    userId,
    roomType,
    onOffer,
    onAnswer,
    onIceCandidate,
    onUserJoined,
    onUserLeft,
    onError
  } = options;

  const [isConnected, setIsConnected] = useState(webSocketManager.getIsConnected());
  const subscriptionIdsRef = useRef<string[]>([]);

  // Subscribe to all room topics
  useEffect(() => {
    const topicPrefix = roomType === 'livestream' 
      ? `/topic/livestream/${roomId}`
      : `/topic/call/${roomId}`;

    debugLogger.log('WebRTCSignaling', 'Subscribing to room topics', { roomId, roomType });

    // Subscribe to offer
    const offerSubId = webSocketManager.subscribe(`${topicPrefix}/offer`, (data: any) => {
      debugLogger.log('WebRTCSignaling', 'Received offer', data);
      if (data.senderId !== userId && onOffer) {
        onOffer(data.senderId, data.offer);
      }
    });
    subscriptionIdsRef.current.push(offerSubId);

    // Subscribe to answer
    const answerSubId = webSocketManager.subscribe(`${topicPrefix}/answer`, (data: any) => {
      debugLogger.log('WebRTCSignaling', 'Received answer', data);
      if (data.senderId !== userId && onAnswer) {
        onAnswer(data.senderId, data.answer);
      }
    });
    subscriptionIdsRef.current.push(answerSubId);

    // Subscribe to ICE candidate
    const iceCandidateSubId = webSocketManager.subscribe(`${topicPrefix}/ice-candidate`, (data: any) => {
      debugLogger.log('WebRTCSignaling', 'Received ICE candidate', data);
      if (data.senderId !== userId && onIceCandidate) {
        onIceCandidate(data.senderId, data.candidate);
      }
    });
    subscriptionIdsRef.current.push(iceCandidateSubId);

    // Subscribe to user joined
    const joinSubId = webSocketManager.subscribe(`${topicPrefix}/user-joined`, (data: any) => {
      debugLogger.log('WebRTCSignaling', 'User joined', data);
      if (data.userId !== userId && onUserJoined) {
        onUserJoined(data.userId);
      }
    });
    subscriptionIdsRef.current.push(joinSubId);

    // Subscribe to user left
    const leaveSubId = webSocketManager.subscribe(`${topicPrefix}/user-left`, (data: any) => {
      debugLogger.log('WebRTCSignaling', 'User left', data);
      if (data.userId !== userId && onUserLeft) {
        onUserLeft(data.userId);
      }
    });
    subscriptionIdsRef.current.push(leaveSubId);

    // Cleanup: Unsubscribe on unmount
    return () => {
      debugLogger.log('WebRTCSignaling', 'Cleaning up subscriptions');
      subscriptionIdsRef.current.forEach((subId) => {
        webSocketManager.unsubscribe(subId);
      });
      subscriptionIdsRef.current = [];
    };
  }, [roomId, userId, roomType, onOffer, onAnswer, onIceCandidate, onUserJoined, onUserLeft]);

  // Listen to connection state changes
  useEffect(() => {
    const handleConnectionState = (connected: boolean) => {
      setIsConnected(connected);
      if (!connected && onError) {
        onError(new Error('WebSocket disconnected'));
      }
    };

    webSocketManager.addConnectionStateListener(handleConnectionState);

    return () => {
      webSocketManager.removeConnectionStateListener(handleConnectionState);
    };
  }, [onError]);

  // Send offer
  const sendOffer = useCallback((targetId: string, offer: RTCSessionDescriptionInit) => {
    const destination = roomType === 'livestream'
      ? `/app/livestream/${roomId}/offer`
      : `/app/call/${roomId}/offer`;

    webSocketManager.publish(destination, {
      senderId: userId,
      targetId,
      offer
    });

    debugLogger.log('WebRTCSignaling', 'Sent offer', { targetId });
  }, [roomId, userId, roomType]);

  // Send answer
  const sendAnswer = useCallback((targetId: string, answer: RTCSessionDescriptionInit) => {
    const destination = roomType === 'livestream'
      ? `/app/livestream/${roomId}/answer`
      : `/app/call/${roomId}/answer`;

    webSocketManager.publish(destination, {
      senderId: userId,
      targetId,
      answer
    });

    debugLogger.log('WebRTCSignaling', 'Sent answer', { targetId });
  }, [roomId, userId, roomType]);

  // Send ICE candidate
  const sendIceCandidate = useCallback((targetId: string, candidate: RTCIceCandidateInit) => {
    const destination = roomType === 'livestream'
      ? `/app/livestream/${roomId}/ice-candidate`
      : `/app/call/${roomId}/ice-candidate`;

    webSocketManager.publish(destination, {
      senderId: userId,
      targetId,
      candidate
    });

    debugLogger.log('WebRTCSignaling', 'Sent ICE candidate', { targetId });
  }, [roomId, userId, roomType]);

  // Notify join
  const notifyJoin = useCallback(() => {
    const destination = roomType === 'livestream'
      ? `/app/livestream/${roomId}/join`
      : `/app/call/${roomId}/join`;

    webSocketManager.publish(destination, { userId });

    debugLogger.log('WebRTCSignaling', 'Notified join');
  }, [roomId, userId, roomType]);

  // Notify leave
  const notifyLeave = useCallback(() => {
    const destination = roomType === 'livestream'
      ? `/app/livestream/${roomId}/leave`
      : `/app/call/${roomId}/leave`;

    webSocketManager.publish(destination, { userId });

    debugLogger.log('WebRTCSignaling', 'Notified leave');
  }, [roomId, userId, roomType]);

  return {
    isConnected,
    sendOffer,
    sendAnswer,
    sendIceCandidate,
    notifyJoin,
    notifyLeave
  };
};
